import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { DataGridPro, GridRowId } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../constants/strings/routes';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import { useFilterUrlSync } from '../../hooks/use-filter-url-sync';
import { FooterAction, GridFooter } from '../grid-components/grid-footer';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../grid-components/grid-types';
import { NoRowsOverlay } from '../grid-components/no-rows-or-error-overlay';
import { PanelTitle } from '../grid-components/panel-title';
import { useGridPagination } from '../grid-components/use-grid-pagination';
import { useGridSelection } from '../grid-components/use-grid-selection';
import { ClientInsurerContractDrawerContent } from './client-insurer-contract-drawer-content';
import { ClientInsurerIndexFilterBar } from './client-insurer-index-filter-bar';
import {
  ClientInsurerIndexFilterModel,
  defaultClientInsurerIndexFilters,
} from './client-insurer-index-types';
import {
  allHaveActiveContracts,
  ClientInsurerIndexRow,
} from './client-insurer-index-utils';
import { UpdateBillingTypePanel } from './update-billing-type-panel';
import { useClientInsurerIndexColumns } from './use-client-insurer-index-columns';
import { useClientInsurerIndexQueries } from './use-client-insurer-index-queries';

export const CLIENT_INSURER_INDEX_GRID_TEST_ID = 'client-insurer-index-grid';

const getRowId = (row: ClientInsurerIndexRow): GridRowId => row.id;

const ClientInsurerIndexGrid = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent } = useDynamicDrawerContext();
  const navigate = useNavigate();

  const [filterParams, setFilterParams] =
    useFilterUrlSync<ClientInsurerIndexFilterModel>(
      defaultClientInsurerIndexFilters,
    );

  const onViewContract = useCallback(
    (ciId: number) => {
      setDrawerContent(
        <ClientInsurerContractDrawerContent clientInsurerId={ciId} />,
        <HHTypography hhVariant="section-title">Contract Details</HHTypography>,
        '30%',
        'temporary',
      );
    },
    [setDrawerContent],
  );

  const columns = useClientInsurerIndexColumns({ onViewContract });

  const {
    paginationModel,
    setPaginationModel,
    handlePaginationChange,
    handlePageOptionChange,
  } = useGridPagination({ page: 0, pageSize: 25 });

  const {
    rows,
    rowCount,
    isLoading,
    hasError,
    clientsData,
    clientsError,
    insurersData,
    insurersError,
    partnershipsData,
    partnershipsError,
    billingTypesData,
    referenceDataLoading,
    refetch,
  } = useClientInsurerIndexQueries(filterParams, paginationModel);

  const {
    selectedIds,
    handleRowSelectionChange,
    clearAllSelections,
    selectedRows,
    visibleSelectedIds,
  } = useGridSelection(rows, getRowId, filterParams);

  const handleBatchCreateContract = (): void => {
    const ids = selectedIds.join(',');
    navigate(
      `${routes.billing.home}${routes.billing.batchContractCreate}?ids=${ids}`,
    );
  };

  const handleBulkUpdateBillingType = (): void => {
    setDrawerContent(
      <UpdateBillingTypePanel
        selectedRows={selectedRows}
        billingTypes={billingTypesData}
        onClearSelections={clearAllSelections}
        onSuccess={refetch}
      />,
      <PanelTitle
        actionType={ActionTypeDisplayText.UPDATE_BILLING_TYPE}
        rowCount={selectedRows.length}
        gridLevel={GridLevelName.CLIENT_INSURER}
      />,
      '50%',
      'temporary',
    );
  };

  const getBulkActions = (): FooterAction[] => [
    {
      action: handleBatchCreateContract,
      actionLabel: 'Batch Create Contract',
      disabled: selectedIds.length === 0,
    },
    {
      action: handleBulkUpdateBillingType,
      actionLabel: 'Update Billing Type',
      disabled:
        selectedIds.length === 0 || !allHaveActiveContracts(selectedRows),
    },
  ];

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={6}
      justifyContent="space-between"
      minHeight="0"
      data-testid={CLIENT_INSURER_INDEX_GRID_TEST_ID}
    >
      <ClientInsurerIndexFilterBar
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        setPaginationModel={setPaginationModel}
        clients={clientsData}
        clientsError={clientsError}
        insurers={insurersData}
        insurersError={insurersError}
        partnerships={partnershipsData}
        partnershipsError={partnershipsError}
        billingTypes={billingTypesData}
        loading={referenceDataLoading}
      />
      <HHStack direction="column" flex={1} minHeight="0">
        <DataGridPro
          columns={columns}
          rows={rows}
          loading={isLoading}
          getRowId={getRowId}
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          initialState={{
            columns: {
              columnVisibilityModel: {
                coreContractRule: false,
                apPartnershipName: false,
                apBillingType: false,
              },
            },
          }}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection={canWrite}
          disableMultipleRowSelection={false}
          rowSelectionModel={visibleSelectedIds}
          onRowSelectionModelChange={handleRowSelectionChange}
          pinnedColumns={{ left: ['__check__'], right: ['actions'] }}
          hideFooter
          {...(process.env.NODE_ENV === 'test' && {
            disableVirtualization: true,
          })}
          slots={{
            noResultsOverlay: NoRowsOverlay,
            noRowsOverlay: NoRowsOverlay,
          }}
          slotProps={{
            noResultsOverlay: { error: hasError } as Record<string, boolean>,
            noRowsOverlay: { error: hasError } as Record<string, boolean>,
          }}
        />
        <GridFooter
          page={paginationModel.page}
          rowsPerPage={paginationModel.pageSize}
          count={rowCount}
          onPageChange={handlePaginationChange}
          onRowsPerPageChange={handlePageOptionChange}
          selectedRows={selectedRows}
          actions={canWrite ? getBulkActions() : undefined}
          gridLevel={GridLevelName.CLIENT_INSURER}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </HHStack>
    </HHStack>
  );
};

export default ClientInsurerIndexGrid;
